/**
 * True when a URL is one of Google's auto-generated default avatars (the generic
 * silhouette) rather than a real uploaded profile photo.
 *
 * Google serves real user photos from the `/a/<token>` content path on
 * `googleusercontent.com`, and auto-generated defaults from the `/a-/<token>`
 * path (plus the explicit `/a/default-user` form). Some Google Workspace
 * accounts expose only a default avatar through the OIDC `picture` claim the app
 * signs in with, even when a real photo exists in the directory. Treating those
 * as "no photo" lets `UserAvatar` fall back to the person initials instead of an
 * indistinguishable silhouette.
 *
 * This is a heuristic stopgap keyed on Google's URL shape: a false positive only
 * downgrades a real photo to the initials/icon fallback (the intended look for
 * photo-less people), never a broken image. The durable fix is to source photos
 * from the Admin SDK Directory API, which returns the directory photo regardless
 * of OIDC visibility.
 */
export function isDefaultGoogleAvatarUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  const { hostname, pathname } = parsed;
  if (hostname !== 'googleusercontent.com' && !hostname.endsWith('.googleusercontent.com')) {
    return false;
  }

  return pathname.startsWith('/a-/') || pathname.startsWith('/a/default-user');
}
